import * as React from 'react';
import {IDateRange, isValEmpty, useCancellableSummon} from '@pluto-tv/assemble';
import {cloneDeep, throttle} from 'lodash-es';
import {IChannel} from 'models/channels';
import {IActiveRegion} from 'models/activeRegions';
import {IElasticSearchRes, IListPayload, IListQuery} from 'models/generic';

import {useUserRegions} from 'helpers/useUserRegions';

interface IChannelSearch extends Partial<Omit<IChannel, 'published' | 'activeRegion'>>, IListQuery {
  // Author needs to be simplified from an object to a string
  author?: string;
  // Certain fields are expected to be a string representation of a boolean in the API
  isUsed?: string;
  promotional?: string;
  liveBroadcast?: string;
  published?: string;
  // Active Region needs to be an array
  activeRegion?: string[];
  // regionFilter -> territories
  territories?: string[];
}

export interface IChannelMetaSearch {
  uploadDate?: IDateRange;
  duration?: IChannel;
  window?: 'day' | 'week' | 'month';
}

interface IUseChannelSearch {
  channel?: IListPayload<IChannel>;
  isError: boolean;
  isFetching: boolean;
  rowsPerPage: 25 | 50 | 75 | 100;
  setRowsPerPage;
  page: number;
  setPage;
  sort: string;
  setSort;
  clearSearch: (doSearch?: boolean) => void;
  search: (searchModel?: Partial<IChannel>, searchMeta?: IChannelMetaSearch, sort?: string) => void;
}

const transformReq = (
  model: Partial<IChannel>,
  page: number,
  perPage: 25 | 50 | 75 | 100,
  sort: string,
  searchMeta?: IChannelMetaSearch,
  activeRegions?: IActiveRegion[],
  // territories?: {[id: string]: ITerritory[]},
): IChannelSearch => {
  const searchObj = cloneDeep(model) as IChannelSearch;

  if (model.activeRegion) {
    searchObj.activeRegion = [model.activeRegion];
  } else {
    searchObj.activeRegion = activeRegions?.map(ar => ar.code.toLowerCase());
  }

  return {
    ...searchObj,
    sort,
    limit: perPage,
    offset: page * perPage,
  };

  if (searchMeta) {
    alert('searchMeta');
  }
};

const defaultSearch: Partial<IChannel> = {
  provider: 'jwplatform',
};

const defaultSort = 'createdAt:desc';

export const useChannelSearch = (): IUseChannelSearch => {
  const [isFetching, setIsFetching] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const [channel, setChannel] = React.useState<IListPayload<IChannel>>();

  const [rowsPerPage, setRowsPerPage] = React.useState<25 | 50 | 75 | 100>(25);
  const [page, setPage] = React.useState<number>(0);
  const [sort, setSort] = React.useState<string>(defaultSort);

  const {activeRegions} = useUserRegions();
  const [summon] = useCancellableSummon();

  const clearSearch = async (doSearch = false): Promise<void> => {
    setPage(0);
    setSort(defaultSort);

    if (doSearch) {
      await search({}, {}, defaultSort);
    }
  };

  const search = throttle(
    async (searchModel?: Partial<IChannel>, searchMeta?: IChannelMetaSearch, customSort?: string) => {
      setIsFetching(true);

      const mySort = customSort ? customSort : sort;

      let cleanedModel: Partial<IChannel> = {};

      if (!searchModel || isValEmpty(searchModel)) {
        cleanedModel = cloneDeep(defaultSearch);
      } else {
        Object.keys(searchModel).forEach(key => {
          const fieldName: keyof IChannel = key as keyof IChannel;

          if (!isValEmpty(searchModel[fieldName])) {
            cleanedModel[fieldName] = cloneDeep(searchModel[fieldName]) as any;
          }
        });
      }

      const fullReq = transformReq(cleanedModel, page, rowsPerPage, mySort, searchMeta, activeRegions);

      try {
        const res = await summon.post<IChannelSearch, IElasticSearchRes<IChannel>>('channels', fullReq);
        const data = res.hits.hits.map(hit => hit._source);

        setChannel({
          data,
          metadata: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            totalCount: res.hits.total,
          },
        });
      } catch (e) {
        setIsError(true);
      } finally {
        setIsFetching(false);
      }
    },
    150,
    {leading: false, trailing: true},
  );

  return {
    channel,
    isError,
    isFetching,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    sort,
    setSort,
    search,
    clearSearch,
  };
};

export const errorMsgTransform = (error: string): string => {
  try {
    const errorMsg = error
      .replaceAll('ValidationError: ', '')
      .split(',')
      .map(x => x.split(': ')[1])
      .join(', ');
    return errorMsg;
  } catch (e) {
    return error;
  }
};
